import { FormEvent, useMemo, useState } from "react";
import { useParams } from "react-router";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";

import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import {
    Distribution,
    DistributionWrite,
    useDeleteDistribution,
    useDistributions,
    useUpsertDistribution,
} from "src/api/client-api/distribution/useDistributions";
import { useDistributionReference } from "src/api/sure-api/useDistributionReference";
import { useSnackbar } from "src/utils/useSnackbar";

type FormState = {
    name: string;
    distributionReferenceId: string;
};

const emptyForm: FormState = {
    name: "",
    distributionReferenceId: "",
};

export const DistributionOverview = () => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const { companyId } = useParams();
    const { isFeatureActivated, isLoading: areApplicationParametersLoading } = useApplicationParameters();
    const isManagedDistributionEnabled = isFeatureActivated("FS-manage-my-distribution");
    const { distributions, isLoading: areDistributionsLoading } = useDistributions({
        enabled: isManagedDistributionEnabled,
    });
    const { distributionReference, isLoading: areDistributionReferencesLoading } = useDistributionReference();
    const upsertDistribution = useUpsertDistribution();
    const deleteDistribution = useDeleteDistribution();

    const [editingDistribution, setEditingDistribution] = useState<Distribution | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [form, setForm] = useState<FormState>(emptyForm);

    const referenceById = useMemo(() => {
        return Object.fromEntries((distributionReference ?? []).map((reference) => [reference.id, reference.label]));
    }, [distributionReference]);

    if (areApplicationParametersLoading) {
        return (
            <Box sx={{ padding: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isManagedDistributionEnabled) {
        return (
            <Box sx={{ padding: 4 }}>
                <Alert severity="warning">{t("common.accessDenied", "Access denied")}</Alert>
            </Box>
        );
    }

    if (areDistributionsLoading || areDistributionReferencesLoading) {
        return (
            <Box sx={{ padding: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    const openCreateDialog = () => {
        setEditingDistribution(null);
        setForm(emptyForm);
        setIsDialogOpen(true);
    };

    const openEditDialog = (distribution: Distribution) => {
        setEditingDistribution(distribution);
        setForm({
            name: distribution.name,
            distributionReferenceId: String(distribution.distributionReferenceId),
        });
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setEditingDistribution(null);
        setForm(emptyForm);
        setIsDialogOpen(false);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const payload: DistributionWrite = {
            id: editingDistribution?.id,
            name: form.name.trim(),
            distributionReferenceId: Number(form.distributionReferenceId),
            ownedBy: companyId ? (`/api/organizations/${companyId}` as IRI) : undefined,
        };

        if (payload.name === "" || !Number.isInteger(payload.distributionReferenceId)) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("distribution.validation.required", "Name and reference are required."),
            });

            return;
        }

        upsertDistribution.mutate(payload, {
            onSuccess: () => {
                snackbar.setSnackbarProps({
                    type: "success",
                    message:
                        editingDistribution === null ?
                            t("distribution.create_success", "Distribution created.")
                        :   t("distribution.update_success", "Distribution updated."),
                });
                closeDialog();
            },
            onError: () => {
                snackbar.setSnackbarProps({
                    type: "error",
                    message: t("distribution.save_error", "Unable to save the distribution."),
                });
            },
        });
    };

    const handleDelete = (distribution: Distribution) => {
        if (!window.confirm(t("distribution.delete_confirm", "Delete this distribution?"))) {
            return;
        }

        deleteDistribution.mutate(distribution.id, {
            onSuccess: () => {
                snackbar.setSnackbarProps({
                    type: "success",
                    message: t("distribution.delete_success", "Distribution deleted."),
                });
            },
            onError: () => {
                snackbar.setSnackbarProps({
                    type: "error",
                    message: t("distribution.delete_error", "Unable to delete the distribution."),
                });
            },
        });
    };

    return (
        <Box sx={{ padding: 4 }}>
            <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    gap={2}
                    sx={{ padding: 3 }}
                >
                    <Box>
                        <Typography variant="h1">{t("distribution.title", "Manage distributions")}</Typography>
                        <Typography variant="body-regular" sx={{ color: "text.secondary" }}>
                            {t(
                                "distribution.subtitle",
                                "Configure the platform distributions used for product Level 2 distribution scoring.",
                            )}
                        </Typography>
                    </Box>
                    <Button variant="contained" onClick={openCreateDialog}>
                        {t("distribution.create", "Create a distribution")}
                    </Button>
                </Stack>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("common.name", "Name")}</TableCell>
                                <TableCell>{t("distribution.reference", "Reference")}</TableCell>
                                <TableCell align="right">{t("common.actions", "Actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {distributions.length === 0 ?
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Typography variant="body-regular" sx={{ color: "text.secondary" }}>
                                            {t("distribution.empty", "No distributions have been created yet.")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            :   distributions.map((distribution) => (
                                    <TableRow key={distribution.id}>
                                        <TableCell>{distribution.name}</TableCell>
                                        <TableCell>
                                            {referenceById[distribution.distributionReferenceId] ??
                                                distribution.distributionReferenceId}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={() => openEditDialog(distribution)}
                                                aria-label={t("common.edit", "Edit")}
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDelete(distribution)}
                                                aria-label={t("common.delete", "Delete")}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <Box component="form" onSubmit={handleSubmit}>
                    <DialogTitle>
                        {editingDistribution === null ?
                            t("distribution.create", "Create a distribution")
                        :   t("distribution.edit", "Edit distribution")}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ paddingTop: 1 }}>
                            <TextField
                                label={t("common.name", "Name")}
                                value={form.name}
                                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                                required
                                fullWidth
                            />
                            <TextField
                                select
                                label={t("distribution.reference", "Reference")}
                                value={form.distributionReferenceId}
                                onChange={(event) =>
                                    setForm((prev) => ({ ...prev, distributionReferenceId: event.target.value }))
                                }
                                required
                                fullWidth
                            >
                                {(distributionReference ?? []).map((reference) => (
                                    <MenuItem key={reference.id} value={String(reference.id)}>
                                        {reference.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog}>{t("common.cancel", "Cancel")}</Button>
                        <Button type="submit" variant="contained">
                            {t("common.save", "Save")}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </Box>
    );
};
